import {
  cloneElement, forwardRef, isValidElement, useId,
  type InputHTMLAttributes, type ReactElement, type ReactNode,
  type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';

/**
 * A labelled form field. The label is an eyebrow (plain capitals) above the control.
 * `outstanding` marks a field the job needs before it can complete and does not have
 * yet — amber, and it goes the moment the field is filled. `optional` says so in place.
 */
export function Field({
  label,
  htmlFor,
  outstanding = false,
  optional = false,
  hint,
  className = '',
  children,
}: {
  label: ReactNode;
  htmlFor?: string;
  outstanding?: boolean;
  optional?: boolean;
  hint?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  // The label is only any use if it points at the control, so when no id is given the
  // field supplies one. Without this a field has no accessible name at all.
  const generatedId = useId().replace(/:/g, '');
  const isControl = isValidElement(children)
    && (children.type === TextInput || children.type === Select || children.type === TextArea
      || (typeof children.type === 'string' && ['input', 'select', 'textarea'].includes(children.type)));
  const control = isControl && !(children.props as { id?: string }).id
    ? cloneElement(children as ReactElement<{ id?: string }>, { id: htmlFor ?? generatedId })
    : children;
  const labelFor = htmlFor ?? (control === children ? undefined : generatedId);

  return (
    <div className={`flex min-w-0 flex-col gap-[7px] ${className}`}>
      <label htmlFor={labelFor} className="eyebrow flex flex-wrap items-center gap-x-2 gap-y-0.5">
        {label}
        {outstanding && <OutstandingMark />}
        {optional && !outstanding && <span className="text-[12px] font-normal normal-case tracking-normal text-ink3">Optional</span>}
      </label>
      {control}
      {hint && <p className="text-[13px] text-ink3">{hint}</p>}
    </div>
  );
}

/** "Needed" — the amber marker for a required detail that has not been supplied yet. */
export function OutstandingMark({ children = 'Needed' }: { children?: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-[5px] whitespace-nowrap text-[12px] font-semibold normal-case tracking-normal text-outstanding">
      <span className="h-1.5 w-1.5 rounded-full bg-outstanding" aria-hidden />
      {children}
    </span>
  );
}

type Outstanding = { outstanding?: boolean };

function fieldClass(outstanding: boolean | undefined, extra: string | undefined) {
  return `field-input ${outstanding ? 'is-outstanding' : ''} ${extra ?? ''}`;
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & Outstanding>(
  function TextInput({ outstanding, className, ...rest }, ref) {
    return <input ref={ref} className={fieldClass(outstanding, className)} {...rest} />;
  }
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & Outstanding>(
  function Select({ outstanding, className, children, ...rest }, ref) {
    return <select ref={ref} className={fieldClass(outstanding, className)} {...rest}>{children}</select>;
  }
);

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & Outstanding>(
  function TextArea({ outstanding, className, ...rest }, ref) {
    return <textarea ref={ref} className={fieldClass(outstanding, className)} {...rest} />;
  }
);

/** A stable id for pairing a Field's label with its control. */
export function useFieldId(prefix: string) {
  return `${prefix}-${useId().replace(/:/g, '')}`;
}
